import React from 'react';
import {
    Menu,
    MenuDivider,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuButton,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { isCapabilityEnabled } from 'owa-capabilities';
import { headerFullBleedCapability } from 'owa-capabilities-definitions/lib/headerFullBleedCapability';
import classNames from 'owa-classnames';
import type { OwaHeaderButtonControl, OwaHeaderButtonControls } from 'owa-header-types';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { OVERFLOW_MENU_BUTTON_ID } from '../../constants/constants';
import toggleHeaderPane from '../../orchestration/toggleHeaderPane';
import { overflowMenuButtonTooltip } from '../OwaHeader.locstring.json';
import {
    headerButton,
    buttonIcon,
    buttonFullBleed,
    menuItemBadge,
} from './OwaHeaderButtonGroup.scss';
import OwaHeaderButtonBadge from './OwaHeaderButtonBadge';

const overflowItemIcon = <MoreHorizontalRegular />;

const OwaHeaderOverflowMenu: React.FC<{
    buttons: OwaHeaderButtonControls;
}> = observer(({ buttons }) => {
    const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

    if (!isOverflowing) {
        return null;
    }

    return (
        <Menu>
            <Tooltip content={loc(overflowMenuButtonTooltip)} relationship="description">
                <MenuTrigger disableButtonEnhancement={true}>
                    <MenuButton
                        id={OVERFLOW_MENU_BUTTON_ID}
                        ref={ref}
                        aria-label={loc(overflowMenuButtonTooltip)}
                        icon={overflowItemIcon}
                        className={classNames(
                            headerButton,
                            isCapabilityEnabled(headerFullBleedCapability) && buttonFullBleed
                        )}
                    ></MenuButton>
                </MenuTrigger>
            </Tooltip>

            <MenuPopover>
                <MenuList>
                    {buttons.map(button => {
                        return <OverflowMenuItem key={button.id} {...button} />;
                    })}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
}, 'OwaHeaderOverflowMenu');

const OverflowMenuItem: React.FC<OwaHeaderButtonControl> = observer(
    ({ id, renderLabel, renderIcon, renderTooltip, onClick, pane, badge, overflowItem }) => {
        const isOverflowItemVisible = useIsOverflowItemVisible(id);

        const handleClick = React.useCallback(() => {
            onClick?.();
            if (pane) {
                toggleHeaderPane(pane.id);
            }
        }, [pane, onClick]);

        const itemIcon = useComputedValue(() => {
            return (
                <div className={buttonIcon}>
                    {renderIcon()}
                    {badge?.getCount && (
                        <OwaHeaderButtonBadge
                            id={id}
                            getCount={badge.getCount}
                            className={menuItemBadge}
                        />
                    )}
                </div>
            );
        }, [renderIcon, id, badge?.getCount]);

        if (isOverflowItemVisible) {
            return null;
        }

        const label = renderLabel();
        const tooltipContent = renderTooltip ? renderTooltip() : label;

        const menuItem = (
            <MenuItem icon={itemIcon} onClick={handleClick}>
                {label}
            </MenuItem>
        );

        const menuItemWithTooltip = (
            <Tooltip content={tooltipContent} relationship="description">
                {menuItem}
            </Tooltip>
        );

        return (
            <>
                {overflowItem?.divider === 'before' && <MenuDivider />}
                {menuItemWithTooltip}
                {overflowItem?.divider === 'after' && <MenuDivider />}
            </>
        );
    },
    'OverflowMenuItem'
);

export default OwaHeaderOverflowMenu;
